import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  PlayIcon,
  PauseIcon,
  StopIcon,
  ArrowPathIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';

/**
 * Compact shift switcher for the header. Lives in the empty space next
 * to the profile chip so operators don't have to navigate to /shifts
 * just to clock in/out.
 *
 * Visible only to roles that actually have shifts (operator/admin/superadmin).
 * The button shows the current state; clicking opens a small popover with
 * the relevant actions for that state.
 */

type ShiftStatus = 'offline' | 'online' | 'break' | 'busy';

interface ShiftStatusData {
  shiftStatus: ShiftStatus;
  status?: ShiftStatus;
  shiftStartTime?: string | null;
}

const STATUS_META: Record<ShiftStatus, { label: string; dot: string; ring: string }> = {
  online:  { label: 'На смене',    dot: 'bg-success',         ring: 'ring-success/30' },
  offline: { label: 'Не на смене', dot: 'bg-muted-foreground', ring: 'ring-border' },
  break:   { label: 'Перерыв',     dot: 'bg-status-paused',   ring: 'ring-status-paused/30' },
  busy:    { label: 'Занят',       dot: 'bg-destructive',     ring: 'ring-destructive/30' },
};

const ShiftQuickControl: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ShiftStatus>('offline');
  const [open, setOpen] = useState(false);
  const [busyAction, setBusyAction] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const hasShifts =
    user?.role === 'operator' || user?.role === 'admin' || user?.role === 'superadmin';

  const normalize = useCallback((raw: ShiftStatusData | undefined): ShiftStatus => {
    if (!raw) return 'offline';
    const v = (raw.shiftStatus || raw.status || 'offline') as ShiftStatus;
    return (['online', 'offline', 'break', 'busy'] as ShiftStatus[]).includes(v) ? v : 'offline';
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!hasShifts) return;
    try {
      const res = await api.get('/api/shifts/status');
      if (res.data?.success) {
        setStatus(normalize(res.data.data));
      }
    } catch {
      // Silent: header widget shouldn't shout on transient network blips.
    }
  }, [hasShifts, normalize]);

  // Initial fetch + refetch when the tab regains focus so a shift started
  // on the /shifts page is reflected here without a manual reload.
  useEffect(() => {
    fetchStatus();
    const onFocus = () => fetchStatus();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchStatus]);

  // Close popover on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const run = useCallback(
    async (
      path: string,
      okMsg: string,
      errMsg: string,
      body?: Record<string, unknown>,
      onAfter?: () => void,
    ) => {
      setBusyAction(true);
      try {
        const res = await api.post(path, body ?? {});
        if (res.data?.success) {
          setStatus(normalize(res.data.data));
          toast.success(okMsg);
          onAfter?.();
        } else {
          toast.error(res.data?.message || errMsg);
        }
      } catch (e: any) {
        toast.error(e?.response?.data?.message || errMsg);
      } finally {
        setBusyAction(false);
        setOpen(false);
      }
    },
    [normalize],
  );

  const startShift  = () => run('/api/shifts/start',  'Смена начата',        'Ошибка при начале смены', { notes: 'Смена начата' });
  const takeBreak   = () => run('/api/shifts/break',  'Перерыв начат',       'Ошибка при начале перерыва');
  const resumeShift = () => run('/api/shifts/resume', 'Смена возобновлена',  'Ошибка при возобновлении смены');

  // Завершение смены может упереться в открытые заявки — там нужна
  // модалка передачи. Полноценная логика живёт на /shifts, поэтому если
  // API вернёт 4xx с подсказкой про открытые тикеты — отправляем туда.
  const endShift = async () => {
    if (!window.confirm('Завершить смену?')) return;
    setBusyAction(true);
    try {
      const res = await api.post('/api/shifts/end');
      if (res.data?.success) {
        setStatus(normalize(res.data.data));
        toast.success('Смена завершена');
      } else {
        toast.error(res.data?.message || 'Ошибка при завершении смены');
      }
    } catch (e: any) {
      const data = e?.response?.data;
      if (data?.data?.openTickets || data?.data?.hasOpenTickets) {
        toast('Есть открытые заявки — закройте смену через раздел «Смены»', { icon: 'ℹ️' });
        navigate('/shifts');
      } else {
        toast.error(data?.message || 'Ошибка при завершении смены');
      }
    } finally {
      setBusyAction(false);
      setOpen(false);
    }
  };

  if (!hasShifts) return null;

  const meta = STATUS_META[status];

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busyAction}
        title={`Смена: ${meta.label}`}
        className={`flex items-center gap-2 h-9 pl-2 pr-3 rounded-full border border-border bg-card hover:bg-accent transition-colors text-sm text-foreground/80 ${meta.ring} ring-1`}
      >
        <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
        <ClockIcon className="h-4 w-4 text-foreground/60 hidden sm:block" />
        <span className="hidden md:inline font-medium">{meta.label}</span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-56 rounded-lg border border-border bg-card shadow-lg z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-border">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Смена</p>
            <p className="text-sm font-medium text-foreground">{meta.label}</p>
          </div>
          <div className="py-1">
            {status === 'offline' && (
              <ActionRow icon={PlayIcon} label="Начать смену" onClick={startShift} disabled={busyAction} tone="success" />
            )}

            {status === 'online' && (
              <>
                <ActionRow icon={PauseIcon} label="Перерыв" onClick={takeBreak} disabled={busyAction} />
                <ActionRow icon={StopIcon}  label="Завершить смену" onClick={endShift} disabled={busyAction} tone="danger" />
              </>
            )}

            {(status === 'break' || status === 'busy') && (
              <>
                <ActionRow
                  icon={ArrowPathIcon}
                  label={status === 'break' ? 'Продолжить смену' : 'Снова свободен'}
                  onClick={resumeShift}
                  disabled={busyAction}
                  tone="success"
                />
                <ActionRow icon={StopIcon} label="Завершить смену" onClick={endShift} disabled={busyAction} tone="danger" />
              </>
            )}

            <div className="border-t border-border my-1" />
            <ActionRow
              icon={ClockIcon}
              label="Подробнее…"
              onClick={() => { setOpen(false); navigate('/shifts'); }}
              disabled={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

interface ActionRowProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  disabled: boolean;
  tone?: 'success' | 'danger';
}

const ActionRow: React.FC<ActionRowProps> = ({ icon: Icon, label, onClick, disabled, tone }) => {
  const toneCls =
    tone === 'success'
      ? 'text-success hover:bg-success/10'
      : tone === 'danger'
      ? 'text-destructive hover:bg-destructive/10'
      : 'text-foreground hover:bg-accent';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${toneCls}`}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </button>
  );
};

export default ShiftQuickControl;
